import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import urls from '../util/urls';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';

interface FilterParams {
  friendlyName?: string;
  id?: string;
  origin: string;
  searchKey?: string;
  searchValue?: string | boolean;
}

interface SavedFilter {
  projectId: string;
  sequenceType: 'somatic' | 'germline';
  variantType:
    | 'shortVariant'
    | 'copyNumberVariant'
    | 'structuralVariant'
    | 'expression';
  name?: string;
  description?: string;
  filters?: Array<FilterParams>;
}

interface UpdateFilter {
  id: string;
  oldFilter: SavedFilter;
  newFilter: SavedFilter;
}

export default class OESavedFiltersService {
  oeSavedFiltersAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.oeSavedFiltersAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  createFilter =
    (auth: ServiceAuth) =>
    ({
      projectId,
      sequenceType,
      variantType,
      name,
      description,
      filters,
    }: SavedFilter) => {
      return this.oeSavedFiltersAPI(auth).post(
        urls.api.genomics.createOmicsFilter(),
        {
          project: projectId,
          sequenceType,
          variantType,
          name,
          description,
          params: {
            filters,
          },
        },
      );
    };

  deleteFilter = (auth: ServiceAuth) => async (id: string) => {
    return this.oeSavedFiltersAPI(auth).delete(
      urls.api.genomics.deleteOmicsFilter(id),
    );
  };

  updateFilter =
    (auth: ServiceAuth) =>
    async ({ id, newFilter, oldFilter }: UpdateFilter) => {
      const body = {
        name:
          newFilter.name !== oldFilter.name ? newFilter.name : oldFilter.name,
        description:
          newFilter.description !== oldFilter.description
            ? newFilter.description
            : oldFilter.description,
        params: {
          filters: newFilter.filters,
        },
      };

      return this.oeSavedFiltersAPI(auth).patch(
        urls.api.genomics.updateOmicsFilter(id),
        body,
      );
    };

  putDefaultFilter = (auth: ServiceAuth) => (filterId: string) => {
    return this.oeSavedFiltersAPI(auth).put(
      urls.api.genomics.defaultOmicsFilter(),
      {
        id: filterId,
      },
    );
  };
}
